import React, { useEffect, useRef } from 'react';
import { animate, useMotionValue, useReducedMotion } from 'framer-motion';
import { Car, Handshake, MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AnimatedRouteMapProps {
  className?: string;
}

// Coordinate space the route path is authored in. Car/pin positions are
// converted from these units to percentages so the whole scene scales with
// its container instead of being pixel-locked.
const VIEWBOX_WIDTH = 400;
const VIEWBOX_HEIGHT = 200;
const START_POINT = { x: 32, y: 156 };
const END_POINT = { x: 368, y: 40 };
const ROUTE_PATH = 'M 32 156 C 110 70 170 190 240 110 C 290 50 320 70 368 40';

const ANIMATION_DURATION_SECONDS = 5.5;

const toPercent = (point: { x: number; y: number }) => ({
  left: `${(point.x / VIEWBOX_WIDTH) * 100}%`,
  top: `${(point.y / VIEWBOX_HEIGHT) * 100}%`,
});

/**
 * Decorative hero centerpiece: a dashed SVG route between a pickup and
 * drop-off pin, with a car icon that continuously travels the path. Purely a
 * client-side visual — no real routing or fare logic.
 *
 * It used to carry an "Estimated fare" badge ticking from $4.50 to $18.50 off
 * `Math.random()`. Decorative in the code, indistinguishable from a live quote
 * on the screen — the same class of thing as the invented competitor prices
 * and the fabricated contact details this repo has already removed. The chip
 * below states what the product does instead of inventing a number to prove
 * it.
 */
const AnimatedRouteMap: React.FC<AnimatedRouteMapProps> = ({ className }) => {
  const pathRef = useRef<SVGPathElement>(null);
  const carRef = useRef<HTMLDivElement>(null);
  const progress = useMotionValue(0);
  const prefersReducedMotion = useReducedMotion();

  // Drive the car along the path.
  useEffect(() => {
    if (prefersReducedMotion) {
      progress.set(0.5);
      return;
    }

    const controls = animate(progress, 1, {
      duration: ANIMATION_DURATION_SECONDS,
      ease: 'easeInOut',
      repeat: Infinity,
      repeatType: 'loop',
    });

    return () => controls.stop();
  }, [progress, prefersReducedMotion]);

  // Translate progress (0-1) into a pixel position + heading along the path.
  useEffect(() => {
    const updateCarPosition = (latest: number) => {
      const path = pathRef.current;
      const car = carRef.current;
      if (!path || !car) return;

      const length = path.getTotalLength();
      const point = path.getPointAtLength(latest * length);
      const lookahead = path.getPointAtLength(Math.min(latest + 0.01, 1) * length);
      const angle = Math.atan2(lookahead.y - point.y, lookahead.x - point.x) * (180 / Math.PI);
      const clampedAngle = Math.max(-30, Math.min(30, angle));

      car.style.left = `${(point.x / VIEWBOX_WIDTH) * 100}%`;
      car.style.top = `${(point.y / VIEWBOX_HEIGHT) * 100}%`;
      car.style.transform = `translate(-50%, -50%) rotate(${clampedAngle}deg)`;
    };

    updateCarPosition(progress.get());
    const unsubscribe = progress.on('change', updateCarPosition);
    return unsubscribe;
  }, [progress]);

  const startPos = toPercent(START_POINT);
  const endPos = toPercent(END_POINT);

  return (
    <div className={cn('relative aspect-[2/1] w-full', className)}>
      <svg
        viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
        preserveAspectRatio="none"
        className="absolute inset-0 h-full w-full"
        aria-hidden="true"
        focusable="false"
      >
        <path
          ref={pathRef}
          d={ROUTE_PATH}
          fill="none"
          stroke="currentColor"
          strokeOpacity={0.4}
          strokeWidth={3}
          strokeDasharray="7 7"
          strokeLinecap="round"
        />
      </svg>

      {/* Pickup pin */}
      <div
        className="absolute flex flex-col items-center"
        style={{ left: startPos.left, top: startPos.top, transform: 'translate(-50%, -100%)' }}
      >
        <span className="mb-1 rounded-full bg-white/90 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-ride-primary shadow-sm">
          Pickup
        </span>
        <MapPin className="h-6 w-6 fill-white text-white drop-shadow" strokeWidth={1.5} />
      </div>

      {/* Drop-off pin */}
      <div
        className="absolute flex flex-col items-center"
        style={{ left: endPos.left, top: endPos.top, transform: 'translate(-50%, -100%)' }}
      >
        <span className="mb-1 rounded-full bg-white/90 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-ride-primary shadow-sm">
          Drop-off
        </span>
        <MapPin className="h-6 w-6 fill-white text-white drop-shadow" strokeWidth={1.5} />
      </div>

      {/* Car traveling the route */}
      <div
        ref={carRef}
        className="absolute z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white text-ride-primary shadow-lg"
        style={{ left: startPos.left, top: startPos.top, transform: 'translate(-50%, -50%)' }}
      >
        <Car className="h-5 w-5" aria-hidden="true" />
      </div>

      {/* What the product does, rather than a number pretending to be a quote. */}
      <div className="absolute bottom-0 right-0 flex items-center gap-2 rounded-lg bg-white/15 px-3 py-2 backdrop-blur-sm">
        <Handshake className="h-4 w-4 text-white/80" aria-hidden="true" />
        <p className="text-sm font-medium text-white">You and your driver agree the fare</p>
      </div>
    </div>
  );
};

export default AnimatedRouteMap;
